/**
 * Credit model.
 *
 * A credit is denominated in USD and spent on concrete system work. Credits are
 * bought with fiat/stable or with $LEGATE; paying in $LEGATE applies a discount.
 * Rates below are the published tariff from the technical concept, §6.2.
 */

export const CREDIT_USD_PRICE = 0.1;

export type BillableOperation =
  | 'company_enrichment'
  | 'segment_rescore'
  | 'agent_message'
  | 'qualified_conversation'
  | 'playbook_generation'
  | 'whatsapp_template';

export const CREDIT_COSTS: Record<BillableOperation, number> = {
  /** Full profile + signals for one company. */
  company_enrichment: 1,
  /** Recomputing scores across a segment. */
  segment_rescore: 5,
  /** One message generated and sent by the agent. */
  agent_message: 2,
  /** A conversation carried through to qualification. */
  qualified_conversation: 15,
  /** Generating a playbook for a new segment. */
  playbook_generation: 25,
  /** Outbound WhatsApp template — plus Meta's own per-conversation cost. */
  whatsapp_template: 3,
};

export const OPERATION_LABELS: Record<BillableOperation, string> = {
  company_enrichment: 'Company enrichment',
  segment_rescore: 'Segment rescore',
  agent_message: 'Agent message',
  qualified_conversation: 'Qualified conversation',
  playbook_generation: 'Playbook generation',
  whatsapp_template: 'WhatsApp template',
};

export const OPERATION_DESCRIPTIONS: Record<BillableOperation, string> = {
  company_enrichment: 'Full public profile and signal pass for one company.',
  segment_rescore: 'Recomputes semantic, tabular and uplift layers across a segment.',
  agent_message: 'One message written by the agent and delivered through a permitted channel.',
  qualified_conversation: 'A conversation carried to qualification and handed to a person.',
  playbook_generation: 'Angle, tone, offer and timing for a new segment, with bandit variants.',
  whatsapp_template: 'Outbound template send. Meta charges its own per-conversation fee on top.',
};

/**
 * Discount for paying in $LEGATE. The concept specifies a 25–35% band; the
 * effective rate is set on-chain in CreditSink and read by the billing service,
 * so this constant is the display default only.
 */
export const TOKEN_DISCOUNT_BPS_DEFAULT = 3_000;

/** TWAP window used when pricing $LEGATE against WETH. */
export const TWAP_WINDOW_SECONDS = 1_800;

// ── Stake tiers ──────────────────────────────────────────────────────────────
// Staking pays no yield. It unlocks product limits — utility, not a return.

export type StakeTier = 'base' | 'bronze' | 'silver' | 'gold' | 'envoy';

export interface StakeTierSpec {
  key: StakeTier;
  index: number;
  label: string;
  /** Threshold in whole $LEGATE tokens. Fixed for a quarter. */
  threshold: number;
  maxAccounts: number;
  /** -1 means unlimited. */
  monthlyCreditGrant: number;
  queuePriority: number;
  features: string[];
}

export const STAKE_TIERS: StakeTierSpec[] = [
  {
    key: 'base',
    index: 0,
    label: 'Base',
    threshold: 0,
    maxAccounts: 1,
    monthlyCreditGrant: 250,
    queuePriority: 0,
    features: ['One connected account', 'Base credit pool', 'Standard queue'],
  },
  {
    key: 'bronze',
    index: 1,
    label: 'Bronze',
    threshold: 250_000,
    maxAccounts: 3,
    monthlyCreditGrant: 1_000,
    queuePriority: 1,
    features: ['Three accounts', 'Queue priority', 'Data export'],
  },
  {
    key: 'silver',
    index: 2,
    label: 'Silver',
    threshold: 1_000_000,
    maxAccounts: 10,
    monthlyCreditGrant: 4_000,
    queuePriority: 2,
    features: ['Ten accounts', 'Uplift models', 'Custom playbooks'],
  },
  {
    key: 'gold',
    index: 3,
    label: 'Gold',
    threshold: 5_000_000,
    maxAccounts: -1,
    monthlyCreditGrant: 15_000,
    queuePriority: 3,
    features: ['Unlimited accounts', 'Priority models', 'API access'],
  },
  {
    key: 'envoy',
    index: 4,
    label: 'Envoy',
    threshold: 20_000_000,
    maxAccounts: -1,
    monthlyCreditGrant: 50_000,
    queuePriority: 4,
    features: ['White label', 'Isolated worker pool', 'Dedicated support'],
  },
];

export function tierForStake(stakeWholeTokens: number): StakeTierSpec {
  let current = STAKE_TIERS[0]!;
  for (const tier of STAKE_TIERS) {
    if (stakeWholeTokens >= tier.threshold) current = tier;
  }
  return current;
}

/**
 * Sink Vault split, §6.3. Tokens received as payment are distributed by a fixed
 * public formula. Mirrors the on-chain configuration in CreditSink.
 */
export const SINK_SPLIT_BPS = {
  burn: 4_000,
  treasury: 4_500,
  dataRewards: 1_500,
} as const;

export const SINK_SPLIT_LABELS = {
  burn: 'Burned',
  treasury: 'Operating treasury',
  dataRewards: 'Data contribution fund',
} as const;
